// Libs
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AlertDialogButtonType } from 'ui-components/components/alert-dialog';
import { getSession, signIn } from 'next-auth/react';
// App
import { showNetworkErrorDialog } from './show-error-dialogs.utils';
import { hasValidAccessToken } from '../user-access/auth/access-token.utils';
import { UNAUTHORIZED_STATUS } from './api.constants';

const isBrowser = typeof window !== 'undefined';
const TIMEOUT = 1 * 60 * 1000;
const REDIRECTING_TO_SIGN_IN_MESSAGE = 'Redirecting to sign in';

axios.defaults.timeout = TIMEOUT;
axios.defaults.baseURL = '/';

let hasNetworkErrorDialogBeenShown = false;

/**
 * Show the network error dialog once until the api answers again, see specs/api-injoignable.md
 */
const showNetworkErrorDialogOnce = async (): Promise<AlertDialogButtonType> => {
  if (hasNetworkErrorDialogBeenShown) return;

  hasNetworkErrorDialogBeenShown = true;

  return showNetworkErrorDialog();
};

/**
 * Show the right notification dialog according to catched error
 * @param error the error catched
 */
export const showAxiosResponseErrorDialog = async (error: AxiosError): Promise<AlertDialogButtonType> => {
  if (!isBrowser) return;

  const status = error.response && error.response.status;

  if (status === 504 || status === 502) {
    return showNetworkErrorDialogOnce();
  }
};

/**
 * Send the user back to the login page, keeping the page he was trying to reach.
 * An already present callbackUrl is reused as is, so a 401 received on the login
 * page itself cannot nest the login url into its own callbackUrl indefinitely.
 */
const redirectToSignIn = async (): Promise<void> => {
  const currentUrl = new URL(window.location.href);
  const callbackUrl = currentUrl.searchParams.get('callbackUrl') || window.location.href;

  await signIn(undefined, { callbackUrl });
};

const setupAxiosInterceptors = (): void => {
  const signInRedirection = axios.CancelToken.source();

  /**
   * Redirect on the first 401 only, and cancel the other requests of the page:
   * they fail together once the token expired, and each of them would otherwise
   * start its own redirection
   */
  const redirectToSignInOnce = async (): Promise<void> => {
    const isAlreadyRedirecting = Boolean(signInRedirection.token.reason);

    if (isAlreadyRedirecting) return;

    signInRedirection.cancel(REDIRECTING_TO_SIGN_IN_MESSAGE);
    await redirectToSignIn();
  };

  const onRequestSuccess = async (config: AxiosRequestConfig) => {
    signInRedirection.token.throwIfRequested();
    config.cancelToken = signInRedirection.token;

    const session = await getSession();

    if (hasValidAccessToken(session)) {
      config.headers.common = {
        Authorization: `Bearer ${session.accessToken}`,
      };
    }

    return config;
  };

  const onResponseSuccess = (response: AxiosResponse) => {
    hasNetworkErrorDialogBeenShown = false;

    return response;
  };
  const onResponseError = async (error: AxiosError) => {
    const status = error.response && error.response.status;

    if (isBrowser && status === UNAUTHORIZED_STATUS) {
      await redirectToSignInOnce();

      return Promise.reject(error);
    }

    if (isBrowser) {
      showAxiosResponseErrorDialog(error);
    }

    return Promise.reject(error);
  };

  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
